import { ClipboardEvent, KeyboardEvent, ReactNode, useState } from 'react';
import { cx } from './cx';
import { typography } from './Text';
import { Chip } from './Chip';
import { useT } from '../../modules/i18n';

/** Splits raw text on commas/whitespace into tokens (typed multi-value entry, paste). */
function tokenize(raw: string): string[] {
  return raw
    .split(/[\s,]+/)
    .map(t => t.trim())
    .filter(Boolean);
}

export interface ChipInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  label?: string;
  /** Accessible name for the input when no visible `label` is rendered. */
  ariaLabel?: string;
  placeholder?: string;
  /** Help text under the input; pass a node that reflects the current state. */
  hint?: ReactNode;
  /** Error message; presence also drives the invalid styling. */
  errorMessage?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  /**
   * Normalize a raw entry before it becomes a chip. Return the cleaned value,
   * or `null` to reject it (e.g. failed validation). Defaults to a trim.
   */
  transform?: (raw: string) => string | null;
  fullWidth?: boolean;
  className?: string;
}

const boxBase =
  'flex flex-wrap items-center gap-1.5 min-h-10 px-2.5 py-1.5 rounded-lg bg-surface ' +
  'border border-strong shadow-xs text-fg transition ' +
  'focus-within:border-brand-300 focus-within:ring-4 focus-within:ring-[rgb(var(--c-ring)/0.24)]';

/**
 * Chip / token input — free-text entry where pressing Enter (or comma) turns the
 * typed text into a removable {@link Chip}. Pasting a delimited string (commas
 * and/or whitespace) commits several chips at once. Backspace on an empty field
 * removes the last chip. Unlike {@link MultiSelect} (pick from a fixed option
 * list), values are typed by the user; pass `transform` to normalize/validate
 * each one. Foundation + Chip primitive, Storybook-able (no Backstage deps).
 */
export const ChipInput = ({
  value,
  onChange,
  label,
  ariaLabel,
  placeholder,
  hint,
  errorMessage,
  isRequired,
  isDisabled,
  transform,
  fullWidth = false,
  className,
}: ChipInputProps) => {
  const t = useT();
  const [draft, setDraft] = useState('');
  const normalize = transform ?? ((s: string) => s.trim() || null);

  const commitTokens = (tokens: string[]) => {
    const additions: string[] = [];
    for (const tok of tokens) {
      const clean = normalize(tok);
      if (clean && !value.includes(clean) && !additions.includes(clean)) {
        additions.push(clean);
      }
    }
    if (additions.length) onChange([...value, ...additions]);
  };

  const commit = () => {
    commitTokens(tokenize(draft));
    setDraft('');
  };

  const remove = (v: string) => onChange(value.filter(x => x !== v));

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      remove(value[value.length - 1]);
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    if (/[\s,]/.test(text)) {
      e.preventDefault();
      commitTokens(tokenize(text));
      setDraft('');
    }
  };

  return (
    <div
      className={cx('flex flex-col gap-1.5', fullWidth && 'w-full', className)}
    >
      {label && (
        <span className={cx(typography.label, 'text-fg-secondary')}>
          {label}
          {isRequired && <span className="ml-0.5 text-brand-text">*</span>}
        </span>
      )}
      <div
        className={cx(
          boxBase,
          errorMessage &&
            'border-error-500 focus-within:border-error-500 focus-within:ring-error-500/24',
          isDisabled && 'opacity-60',
        )}
      >
        {value.map(v => (
          <Chip
            key={v}
            label={v}
            size="sm"
            onRemove={isDisabled ? undefined : () => remove(v)}
            removeLabel={t('ui.chipInput.removeAriaLabel', { value: v })}
            isDisabled={isDisabled}
          />
        ))}
        <input
          aria-label={ariaLabel}
          value={draft}
          disabled={isDisabled}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onBlur={commit}
          placeholder={value.length === 0 ? placeholder : undefined}
          className={cx(
            typography.body,
            'min-w-[8rem] flex-1 appearance-none border-0 bg-transparent text-fg outline-none placeholder:text-fg-muted',
          )}
        />
      </div>
      {hint && !errorMessage && (
        <span className={cx(typography.body, 'text-fg-tertiary')}>{hint}</span>
      )}
      {errorMessage && (
        <span className={cx(typography.body, 'text-error')}>
          {errorMessage}
        </span>
      )}
    </div>
  );
};
